/**
 * Design master upload (Phase 65 / BLU-594 PR3b).
 *
 * Drop-file flow: stage the File (object-URL thumb) → mint a presigned GCS PUT
 * (`mintDesignMasterUploadUrl`) → browser PUT the bytes → flip the slot to
 * `uploaded` with the minted key (which becomes the scope's `upload` SourceRef).
 * The Content-Type sent on the PUT MUST equal the one the URL was signed with,
 * so we derive it once and pass the same value to both.
 *
 * Phase 86 (D-01): with a `platenVariantKey` the file is staged under that
 * group's own slot (`platenSlotKey`) and the mint stores it under a distinct
 * name per platen variant (PNG only, enforced server side), so two platen
 * variants of the same design, set and side never overwrite one object.
 */
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { trpc } from '@/lib/trpc';
import { platenSlotKey, slotKey, type DesignSet, type DesignSide, type StagedDesignsApi } from './useStagedDesigns';
import { uploadRefusalKey } from '../lib/upload-refusal';

const CONTENT_TYPE_BY_EXT: Record<string, string> = {
  png: 'image/png',
  tiff: 'image/tiff',
  tif: 'image/tiff',
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
};

function deriveContentType(file: File): string {
  if (file.type && file.type.startsWith('image/')) return file.type;
  const dot = file.name.lastIndexOf('.');
  const ext = dot > 0 ? file.name.slice(dot + 1).toLowerCase() : '';
  return CONTENT_TYPE_BY_EXT[ext] ?? 'application/octet-stream';
}

export interface DesignUploadApi {
  startUpload: (
    productId: string,
    set: DesignSet,
    side: DesignSide,
    designCode: string,
    file: File,
    /** `<platen>:<variant>` for a ruled product's platen variant slot. */
    platenVariantKey?: string,
  ) => Promise<void>;
}

/**
 * `byoProductType`: Phase 86 slice B (86-23), a BYO product's own product
 * type name. The server maps it to the org's type code and stages the file
 * under `{TYPE}-{DESIGN}`, so two products of different types that share one
 * design ID never overwrite each other's staged file. Omitted for every other
 * product (the staged key is unchanged).
 */
export function useDesignUpload(staging: StagedDesignsApi, byoProductType?: string | null): DesignUploadApi {
  const { t } = useTranslation('productSync');
  const mint = trpc.productSync.mintDesignMasterUploadUrl.useMutation();

  const startUpload = useCallback(
    async (
      productId: string,
      set: DesignSet,
      side: DesignSide,
      designCode: string,
      file: File,
      platenVariantKey?: string,
    ) => {
      const slot = platenVariantKey ? platenSlotKey(platenVariantKey, set, side) : slotKey(set, side);
      const contentType = deriveContentType(file);

      // Stage immediately (thumb visible) then transition to uploading.
      staging.setAssignment(productId, slot, {
        kind: 'upload',
        file,
        url: URL.createObjectURL(file),
        state: 'uploading',
      });

      try {
        const minted = await mint.mutateAsync({
          designCode,
          side,
          set,
          fileName: file.name,
          contentType,
          ...(platenVariantKey ? { platenVariantKey } : {}),
          ...(byoProductType ? { productType: byoProductType } : {}),
        });
        const putRes = await fetch(minted.uploadUrl, {
          method: 'PUT',
          headers: { 'Content-Type': contentType },
          body: file,
        });
        if (!putRes.ok) {
          throw new Error(`upload failed (${putRes.status})`);
        }
        staging.patchUpload(productId, slot, { state: 'uploaded', key: minted.key, error: undefined });
      } catch (err) {
        const refusal = uploadRefusalKey(err);
        const message = refusal ? t(refusal) : err instanceof Error ? err.message : t('upload.failed');
        staging.patchUpload(productId, slot, { state: 'error', error: message });
      }
    },
    [mint, staging, t, byoProductType],
  );

  return { startUpload };
}
